"use client";

/**
 * What clients said (montanhas-web#19): the testimonials staff published, as cards in a
 * strip that swipes on a phone and wraps on a desktop, and the rating of every answer
 * beside them. On an event's page they come from the past events of its programme;
 * on Início, the ones staff featured. Only what staff chose is ever here, named by
 * the first name alone or not at all, with a photo of the client only where they
 * allowed one. Nothing per visitor: the server draws it.
 */
import { useState } from "react";
import { Star } from "lucide-react";
import { cdnUrl } from "@/lib/cdn";
import { monthName } from "@/lib/format";
import { track } from "@/lib/analytics";
import type { Testimonial, Testimonials } from "@/lib/types/testimonial";
import { openGallery } from "@/components/event/EventGallery";

/** where the event page's block is, for the rating beside the facts to scroll to */
export const TESTIMONIALS_ANCHOR = "opinioes";

// a testimonial longer than this many characters starts clipped, with Ler mais
const LONG = 220;

/** 4,9 */
export const ratingText = (rating: number) => String(rating).replace(".", ",");

/** "12 opiniões" */
export const countText = (count: number) => `${count} ${count === 1 ? "opinião" : "opiniões"}`;

/** "★ 4,9 · 12 opiniões", or nothing with too few to say */
export function RatingLine({ of, className = "" }: { of: Pick<Testimonials, "rating" | "count"> | null | undefined; className?: string }) {
  if (of?.rating == null) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink ${className}`} aria-label={`${ratingText(of.rating)} em 5, ${countText(of.count)}`}>
      <Star size={15} className="fill-amber text-amber" />
      {ratingText(of.rating)}
      <span className="font-normal text-ink-soft">· {countText(of.count)}</span>
    </span>
  );
}

/** the rating as a chip beside an event's facts; pressed, it scrolls to the testimonials (or does nothing without them) */
export function RatingChip({ of, hasWords }: { of: Pick<Testimonials, "rating" | "count"> | null | undefined; hasWords: boolean }) {
  if (of?.rating == null) return null;
  const go = () => {
    const block = document.getElementById(TESTIMONIALS_ANCHOR);
    if (!block) return;
    block.scrollIntoView({ behavior: "smooth", block: "start" });
    track("select_content", { content_type: "testimonials", item_id: "rating" });
  };
  const chip = "inline-flex items-center gap-1.5 rounded-full bg-amber-soft px-3 py-1.5 text-[12.5px] font-semibold text-[#7a5a2c]";
  const inside = (
    <>
      <Star size={14} className="fill-amber text-amber" />
      {ratingText(of.rating)} · {countText(of.count)}
    </>
  );
  return hasWords ? (
    <button type="button" onClick={go} className={`pressable ${chip}`}>
      {inside}
    </button>
  ) : (
    <span className={chip}>{inside}</span>
  );
}

function Face({ testimonial }: { testimonial: Testimonial }) {
  const { photo, name } = testimonial;
  if (photo) {
    const open = () =>
      openGallery([{ id: testimonial.id, path: photo.path, thumbnail: photo.thumbnail, width: photo.width, height: photo.height }], 0, {
        caption: () => `${name || "Participante"} · ${testimonial.event_title}`,
      });
    return (
      <button type="button" onClick={open} className="pressable h-10 w-10 shrink-0 overflow-hidden rounded-full bg-ground" aria-label={`Fotografia de ${name || "participante"}`}>
        {/* eslint-disable-next-line @next/next/no-img-element -- photos come straight from the CDN in their own sizes */}
        <img src={cdnUrl(photo.thumbnail || photo.path)} alt={`${name || "Participante"} em ${testimonial.event_title}`} className="h-full w-full object-cover" loading="lazy" />
      </button>
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[15px] font-bold text-brand-dark" aria-hidden>
      {(name || "M").slice(0, 1).toUpperCase()}
    </span>
  );
}

function TestimonialCard({ testimonial, className = "" }: { testimonial: Testimonial; className?: string }) {
  const long = testimonial.text.length > LONG;
  const [open, setOpen] = useState(false);
  const when = `${monthName(`${testimonial.month}-01`)} ${testimonial.month.slice(0, 4)}`;
  return (
    <figure className={`card m-0 flex flex-col p-4 ${className}`}>
      <blockquote className={`m-0 whitespace-pre-line text-[14px] leading-[1.45] text-ink ${long && !open ? "line-clamp-5" : ""}`}>«{testimonial.text}»</blockquote>
      {long && (
        <button
          type="button"
          onClick={() => {
            setOpen(!open);
            if (!open) track("select_content", { content_type: "testimonial", item_id: String(testimonial.id) });
          }}
          className="pressable mt-1 self-start text-[13px] font-bold text-brand"
        >
          {open ? "Ler menos" : "Ler mais"}
        </button>
      )}
      <figcaption className="mt-auto flex items-center gap-2.5 pt-3">
        <Face testimonial={testimonial} />
        <span className="min-w-0 leading-tight">
          <b className="block truncate text-[13.5px]">{testimonial.name || "Participante"}</b>
          <span className="block truncate text-[12.5px] text-ink-soft">
            {testimonial.event_title} · {when}
          </span>
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * The cards: a strip that swipes on a phone, a grid of up to three across on a
 * desktop. Nothing with none.
 */
export function TestimonialCards({ testimonials, className = "" }: { testimonials: Testimonial[]; className?: string }) {
  if (!testimonials.length) return null;
  return (
    <div className={`-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] desk:mx-0 desk:grid desk:grid-cols-2 desk:overflow-visible desk:px-0 lg:grid-cols-3 ${className}`}>
      {testimonials.map((t) => (
        <TestimonialCard key={t.id} testimonial={t} className={`snap-start ${testimonials.length > 1 ? "w-[84%] shrink-0 desk:w-auto" : "w-full"}`} />
      ))}
    </div>
  );
}

/** An event's O que disseram: the cards and the rating, or nothing without published words. */
export function EventTestimonials({ of, className = "" }: { of: Testimonials | null | undefined; className?: string }) {
  if (!of?.testimonials?.length) return null;
  return (
    <section id={TESTIMONIALS_ANCHOR} className={`scroll-mt-20 ${className}`}>
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-[17px] font-bold desk:text-[19px]">O que disseram</h2>
        <RatingLine of={of} />
      </div>
      <TestimonialCards testimonials={of.testimonials} />
    </section>
  );
}
